function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import FormField from 'granite-admin/core/components/FormField';
import Select from 'granite-admin/core/components/Select';
import DateRangePicker from './DateRangePicker';
import { formatDate } from './utils';
const getBackDate = (numberOfDays, customFormat) => {
  var d = new Date();
  d.setDate(d.getDate() - numberOfDays);
  return format(d, customFormat);
};
const getThisMonthDateRange = () => {
  let d = new Date();
  let lastDay = format(new Date(d.getFullYear(), d.getMonth() + 1, 0), 'yyyy-MM-dd');
  let startOfMonth = new Date(d.getFullYear(), d.getMonth());
  return [format(startOfMonth, 'yyyy-MM-dd'), lastDay];
};
const getThisQuarterDateRange = () => {
  let d = new Date();
  let m = d.getMonth();
  let fromDate = new Date(d.getFullYear(), m - m % 3, 1);
  let toDate = new Date(d.getFullYear(), m + 3 - m % 3, 0);
  return [format(fromDate, 'yyy-MM-dd'), format(toDate, 'yyy-MM-dd')];
};
const getThisYearDateRange = () => {
  let year = new Date().getFullYear();
  return [format(new Date(year, 0, 1), 'yyy-MM-dd'), format(new Date(year, 11, 31), 'yyy-MM-dd')];
};
const getDateRangeOptionsDefault = customFormat => {
  let today = new Date();
  let yesterday = format(new Date(today.setDate(today.getDate() - 1)), customFormat);
  return [{
    label: 'Last 7 days',
    value: [getBackDate(7, customFormat), yesterday]
  }, {
    label: 'Last 15 days',
    value: [getBackDate(15, customFormat), yesterday]
  }, {
    label: 'Last 30 days',
    value: [getBackDate(30, customFormat), yesterday]
  }, {
    label: 'This Month',
    value: getThisMonthDateRange()
  }, {
    label: 'This Quarter',
    value: getThisQuarterDateRange()
  }, {
    label: 'This Year',
    value: getThisYearDateRange()
  }, {
    label: 'Custom Date Range',
    value: 'VIEW_DATE_PICKER'
  }];
};
const DateRangeOptions = ({
  initialDate,
  customFormat,
  menuPortalTarget,
  onChange,
  name,
  label,
  viewDateFormat,
  getDateRangeOptions,
  ...rest
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [dates, setDates] = useState(null);
  const dateRangeOptions = useMemo(() => getDateRangeOptions ? getDateRangeOptions(customFormat) : getDateRangeOptionsDefault(customFormat), []);
  useEffect(() => {
    if (initialDate) {
      setDates([[formatDate(new Date(initialDate.split(' - ')[0]), customFormat), formatDate(new Date(initialDate.split(' - ')[1]), customFormat)]]);
    }
  }, [initialDate]);
  const selectedValueFromOptions = useMemo(() => {
    return dateRangeOptions[dateRangeOptions.findIndex(i => JSON.stringify([i.value]) == JSON.stringify(dates))];
  }, [dateRangeOptions, dates]);
  useEffect(() => {
    if (selectedValueFromOptions) {
      setSelectedOption(selectedValueFromOptions);
    } else if (dates) {
      setSelectedOption(dateRangeOptions.find(i => i.label === 'Custom Date Range'));
    }
  }, [selectedValueFromOptions, dates]);
  function isValidDate(date) {
    return date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date);
  }
  const handleSelect = dates => {
    if (dates && dates[0] && dates[1]) {
      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[1]);
      onChange({
        sDate: customFormat !== null && isValidDate(startDate) ? format(startDate, customFormat) : startDate,
        eDate: customFormat !== null && isValidDate(endDate) ? format(endDate, customFormat) : endDate
      });
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormField, {
    name: name,
    label: label
  }, /*#__PURE__*/React.createElement(Select, {
    name: "dateRange",
    options: dateRangeOptions,
    labelKey: "label",
    value: selectedOption?.label,
    valueKey: {
      key: 'label',
      reduce: false
    },
    onChange: ({
      value
    }) => {
      if (value.label === 'Custom Date Range') {
        setSelectedOption(value);
      } else {
        handleSelect(value.value);
        setSelectedOption(value);
      }
    },
    closeMenuOnSelect: true,
    isClearable: false,
    menuPortalTarget: menuPortalTarget
  })), selectedOption?.label === 'Custom Date Range' && /*#__PURE__*/React.createElement(FormField, {
    name: 'datePicker',
    label: 'Select Date Range'
  }, /*#__PURE__*/React.createElement(DateRangePicker, _extends({
    name: name,
    label: label,
    initialDate: initialDate,
    onChange: onChange,
    menuPortalTarget: document.body,
    customFormat: customFormat,
    viewDateFormat: viewDateFormat
  }, rest))));
};
DateRangeOptions.defaultProps = {
  customFormat: 'yyyy-MM-dd',
  viewDateFormat: 'dd/MM/yyyy'
};
export default DateRangeOptions;